import { Component, OnInit, ViewChild } from '@angular/core';
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { SocialSignInButtonComponent } from "../../components/social-sign-in-button/social-sign-in-button.component";
import { PrimaryButtonComponent } from "../../../../shared/components/primary-button/primary-button.component";
import { SeparatorWithTextComponent } from "../../components/separator-with-text/separator-with-text.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { combineLatest, firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/api/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthTokenService } from '../../../../core/services/auth-token.service';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { NavigationShareService } from '../../../../core/services/navigation-share.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [LogoComponent, LabeledInputComponent, SocialSignInButtonComponent, PrimaryButtonComponent, SeparatorWithTextComponent, TranslocoPipe, ReactiveFormsModule, ToastComponent],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.css'
})
export class SignupComponent implements OnInit {
  // Email Inputs
  inputError: string | null = null;
  invalidEmailErrorMessage: string = `Please enter a valid email address`;
  emailRequiredErrorMessage: string = `Email is required`;

  emailForm: FormGroup;

  continueClicked: boolean = false;
  googleButtonClicked: boolean = false;
  linkedinButtonClicked: boolean = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Sales Agent Invite
  agentId: string | null = null;
  promotionCode: string | undefined = undefined;

  constructor(private translocoService: TranslocoService, private formBuilder: FormBuilder, private authService: AuthService, private router: Router, private authToken: AuthTokenService, private activatedRoute: ActivatedRoute, private apiError: ApiErrorService, private shareService: NavigationShareService) {
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    // Populate with translation
    combineLatest([
      this.translocoService.selectTranslate('auth.email_required'),
      this.translocoService.selectTranslate('auth.invalid_email'),
    ]).subscribe(([emailRequired, invalidEmail]) => {
      this.emailRequiredErrorMessage = emailRequired;
      this.invalidEmailErrorMessage = invalidEmail;
    });

    // Populate email with query email if available
    this.activatedRoute.queryParams.subscribe(params => {
      this.emailForm.get('email')?.setValue(params['email'] ?? '');

      this.agentId = params['agent_invite'] ?? null;

      this.promotionCode = params['promotion'] ?? undefined;
    });
  }

  validateEmail(): boolean {
    const emailControl = this.emailForm.get('email');
    const emailValue = emailControl?.value?.trim()

    const requiredError = Validators.required({ value: emailValue } as any);
    if (requiredError) {
      this.inputError = this.emailRequiredErrorMessage;
      return false;
    }

    const emailError = Validators.email({ value: emailValue } as any);
    if (emailError) {
      this.inputError = this.invalidEmailErrorMessage;
      return false;
    }

    this.inputError = null;
    return true;
  }

  async continueClick() {
    // Block multiple button clicks while processing
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    if (!this.validateEmail()) {
      this.continueClicked = false;
      return;
    }

    const emailControl = this.emailForm.get('email');
    const emailValue = emailControl?.value?.trim()

    // Skip email verification if agent id used
    if (this.agentId) {
      this.router.navigate(['/auth/onboarding/'], { queryParams: { step: 1, agent_invite: this.agentId, promotion: this.promotionCode } })

      this.shareService.setState("email", emailValue);
      return;
    }

    // Generate a redirect URL and send the temporary password
    const appUrl = window.location.origin;
    var redirectUrl = `${appUrl}/auth/temp-password?email=${encodeURIComponent(emailValue)}`;

    // Include promotion code if available
    if (this.promotionCode) {
      redirectUrl += `&promotion=${this.promotionCode}`;
    }

    try {
      await firstValueFrom(this.authService.sendTempPassword(emailValue, redirectUrl));
    }
    catch (error) {
      if (error instanceof HttpErrorResponse) {
        // Check if the user already exists
        if (error.status == 400 && error.error?.code == 'SVC-4020') {
          // Send user to sign in page
          this.continueClicked = false;
          this.router.navigate(['/auth/signin'], { queryParams: { email: emailValue } });
          return;
        }

        // Attempting to resend in case of an error
        try {
          await firstValueFrom(this.authService.resendTempPassword(emailValue, redirectUrl));
        }
        catch (resetError) {
          var crid = 'UNKNOWN-CRID';
          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.continueClicked = false;
          return;
        }
      }
      else {
        this.continueClicked = false;
        return;
      }
    }

    this.continueClicked = false;
    this.router.navigate(['/auth/check-email'], { queryParams: { email: emailValue, promotion: this.promotionCode } });
  }

  async googleLoginButtonClick() {
    if (this.googleButtonClicked) {
      return;
    }
    this.googleButtonClicked = true;

    try {
      var response = await firstValueFrom(this.authService.googleAuthorize());

      window.location.href = response.authorization_url;
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.googleButtonClicked = false;
      return;
    }
    this.googleButtonClicked = false;
  }

  async linkedinLoginButtonClick() {
    if (this.linkedinButtonClicked) {
      return;
    }
    this.linkedinButtonClicked = true;

    try {
      var response = await firstValueFrom(this.authService.linkedinAuthorize());

      window.location.href = response.authorization_url;
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.linkedinButtonClicked = false;
      return;
    }
    this.linkedinButtonClicked = false;
  }
}
